import { faHeart } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type { css } from 'styled-components';
import styled from 'styled-components';

import { FullViewportScroll } from '@sorare/core/src/atoms/container/FullViewportScroll';
import type { IconDefinition } from '@sorare/core/src/atoms/icons';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { useSearchCardsContext } from '@sorare/core/src/contexts/searchCards';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useVirtualToggleManager } from '@sorare/core/src/hooks/useVirtualToggleManager';
import type { VirtualToggleFilter } from '@sorare/core/src/lib/filters';
import { VIRTUAL_TOGGLE_FILTERS } from '@sorare/core/src/lib/filters';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { VirtualFilterSegment } from './VirtualFilterSegment';
import { SegmentButton } from './ui';

type VirtualSegment = {
  filter: VirtualToggleFilter;
  icon: IconDefinition | null;
  customCss?: ReturnType<typeof css>;
};

const { favoriteFilter } = VIRTUAL_TOGGLE_FILTERS;

const Root = styled(Horizontal)`
  @media ${tabletAndAbove} {
    flex-wrap: wrap;
  }
`;

const FavoriteFilter = styled.div`
  position: relative;
  padding: 0 var(--unit);
  &:before {
    content: '';
    position: absolute;
    left: 0;
    height: 60%;
    top: 20%;
    width: 1px;
    background-color: var(--c-nd-100);
  }
  &:not(:last-child) {
    &:after {
      content: '';
      position: absolute;
      right: 0;
      height: 70%;
      top: 15%;
      width: 1px;
      background-color: var(--c-nd-100);
    }
  }
`;

type Props = {
  virtualSegments?: VirtualSegment[];
};

export const Segment = ({ virtualSegments: virtualSegmentsProps }: Props) => {
  const virtualSegments = virtualSegmentsProps || [];

  const { up: tabletOrAbove } = useScreenSize('tablet');
  const setVirtualToggle = useVirtualToggleManager();

  const { favoriteFilter: favoriteFilterCurrentRefinement } =
    useSearchCardsContext();

  const initToggleSegments = [
    ...(favoriteFilterCurrentRefinement ? [favoriteFilter.name] : []),
  ];
  const [toggledSegments, setToggledSegments] =
    useState<string[]>(initToggleSegments);

  const clean = () => {
    setVirtualToggle({
      [favoriteFilter.name]: false,
    });
  };

  const onClickAll = () => {
    if (toggledSegments.length > 0) {
      clean();
      setToggledSegments([]);
    }
  };

  return (
    <ConditionalWrapper wrap={!tabletOrAbove} Wrapper={FullViewportScroll}>
      <Root>
        <SegmentButton
          className={classNames({
            checked:
              toggledSegments.length === 0 && !favoriteFilterCurrentRefinement,
          })}
          onClick={onClickAll}
        >
          <BodyM bold>
            <FormattedMessage {...glossary.all} />
          </BodyM>
        </SegmentButton>
        <FavoriteFilter>
          <VirtualFilterSegment
            key={favoriteFilter.name}
            filter={favoriteFilter}
            icon={faHeart}
            onClick={newValue => {
              if (newValue) {
                setToggledSegments([...toggledSegments, favoriteFilter.name]);
              } else {
                setToggledSegments(
                  toggledSegments.filter(s => s !== favoriteFilter.name)
                );
              }
            }}
            needsLogin
          />
        </FavoriteFilter>
        {virtualSegments.map(segment => {
          return (
            <VirtualFilterSegment
              key={segment.filter.name}
              filter={segment.filter}
              cleanableFilters={virtualSegments
                .filter(s => s.filter.name !== segment.filter.name)
                .map(s => s.filter)}
              icon={segment.icon}
              customCss={segment.customCss}
              onClick={newValue => {
                if (newValue) {
                  setToggledSegments([
                    ...(toggledSegments.includes(favoriteFilter.name)
                      ? [favoriteFilter.name]
                      : []),
                    segment.filter.name,
                  ]);
                } else {
                  setToggledSegments(
                    toggledSegments.filter(s => s !== segment.filter.name)
                  );
                }
              }}
            />
          );
        })}
      </Root>
    </ConditionalWrapper>
  );
};
